/**
 * WorkBuddy 统一登录的 PKCE 证明（#130554）
 *
 * ---------------------------------------------------------------------------
 * 为什么只有 workbuddy 这条链路要 PKCE
 * ---------------------------------------------------------------------------
 *
 * 微信扫码与 Google 的回调只带 `code` + Relay 自签的 state，state 一次性消费就够
 * 了。WorkBuddy 这条链路多一层：授权事务（`wb.a.*`）与兑换事务（`wb.e.*`）是两条
 * 记录，浏览器必须自己证明「发起授权的那个标签页」和「来兑换的这个标签页」是同
 * 一个 —— 否则拿到回调 URL 的人就能换走会话。证明方式是本地随机 verifier：
 * 只有它的 S256 摘要进 URL，verifier 本身**从不离开本标签页**。
 *
 * 服务端校验在 `oauthrelay_workbuddy_state.go`：
 * - authorize 要求 `code_challenge_method=S256`、challenge 长度正好 43、严格
 *   base64url 解码后正好 32 字节；
 * - 兑换时要求 verifier 长度 43–128、字符集 `[A-Za-z0-9-._~]`，且
 *   `base64url(SHA256(verifier))` 等于当初那个 challenge。
 *
 * 32 字节随机数的 base64url 正好 43 个字符、且字符集是上面那个集合的子集，两头
 * 的约束同时满足。**不要改成 hex 或别的长度**：hex 是 64 字符，challenge 那条
 * `len == 43` 的硬校验会直接拒。
 */

/** verifier 存储 key 前缀。带 publishableKey，同浏览器多应用互不覆盖。 */
const VERIFIER_KEY_PREFIX = 'workbuddy-cloud.pkce.workbuddy.';

/** 已兑换事务 key 前缀。用途见 {@link WorkBuddyPkceStore.markConsumed}。 */
const CONSUMED_KEY_PREFIX = 'workbuddy-cloud.pkce-done.workbuddy.';

function base64UrlEncode(bytes: Uint8Array): string {
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    // btoa 在浏览器与 Node 18+ 都有；这条路径只在浏览器里走。
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 当前运行时是否具备生成 PKCE 证明的能力。 */
export function hasWebCrypto(): boolean {
    const c = globalThis.crypto as Crypto | undefined;
    return (
        typeof c?.getRandomValues === 'function' &&
        typeof c?.subtle?.digest === 'function' &&
        typeof globalThis.btoa === 'function'
    );
}

/** 32 字节随机 verifier，base64url 后 43 字符。 */
export function createCodeVerifier(): string {
    const bytes = new Uint8Array(32);
    globalThis.crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
}

/** `base64url(SHA256(verifier))` —— 与服务端 `hashOAuthCode` 同一个函数。 */
export async function computeS256Challenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
}

/**
 * verifier 的暂存处。
 *
 * **必须是 sessionStorage，不能是 localStorage**：verifier 的语义是「本标签页本次
 * 跳转」，localStorage 会让同一浏览器里另开的标签页读到并用掉它，等于把证明泄露给
 * 了一个不是发起方的上下文。sessionStorage 跨 top-level 跳转仍保留，正好覆盖
 * 「跳去授权页再跳回来」这一个来回。
 *
 * 也不退回内存：跳转会销毁页面，内存副本回来就没了，整条登录必然失败。存不下就
 * 在发起阶段明确报错，而不是让用户走到回调页才发现换不了会话。
 */
export class WorkBuddyPkceStore {
    private readonly verifierKey: string;
    private readonly consumedKey: string;

    constructor(publishableKey: string) {
        this.verifierKey = VERIFIER_KEY_PREFIX + publishableKey;
        this.consumedKey = CONSUMED_KEY_PREFIX + publishableKey;
    }

    /** sessionStorage 是否真的可读写（隐私模式、部分 WebView 下访问即抛）。 */
    usable(): boolean {
        const storage = this.storage();
        if (!storage) {
            return false;
        }
        try {
            const probe = `${this.verifierKey}.probe`;
            storage.setItem(probe, '1');
            storage.removeItem(probe);
            return true;
        } catch {
            return false;
        }
    }

    /** 写入失败返回 false —— 调用方据此拒绝发起登录。 */
    saveVerifier(verifier: string): boolean {
        try {
            const storage = this.storage();
            if (!storage) {
                return false;
            }
            storage.setItem(this.verifierKey, verifier);
            // 新一次授权作废上一次的「已兑换」标记，否则 state 偶然相同时会被
            // 误判成重复回调。
            storage.removeItem(this.consumedKey);
            return true;
        } catch {
            return false;
        }
    }

    readVerifier(): string | null {
        try {
            return this.storage()?.getItem(this.verifierKey) ?? null;
        } catch {
            return null;
        }
    }

    clearVerifier(): void {
        try {
            this.storage()?.removeItem(this.verifierKey);
        } catch {
            // 清理失败不影响结果正确性：verifier 单独一个没有用，必须配上
            // 同事务的 code + state 才能兑换，而那个事务服务端已经消费了。
        }
    }

    /**
     * 记下刚兑换成功的那个 state。
     *
     * 回调处理在应用里常被调两次（框架的严格模式、路由二次挂载）。第二次时
     * verifier 已经清掉了，如果不认这个标记就会报「缺少证明」，看起来像登录失败，
     * 而其实会话已经建好了。记下 state 才能把第二次识别成重复回调、直接返回空。
     */
    markConsumed(state: string): void {
        try {
            this.storage()?.setItem(this.consumedKey, state);
        } catch {
            // 存不下只会退化成第二次调用报错，不影响已建立的会话。
        }
    }

    isConsumed(state: string): boolean {
        try {
            return !!state && this.storage()?.getItem(this.consumedKey) === state;
        } catch {
            return false;
        }
    }

    private storage(): Storage | null {
        try {
            return globalThis.sessionStorage ?? null;
        } catch {
            return null;
        }
    }
}
